import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Popover,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import debounce from "lodash/debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SubscriptionDialog from "../subscription/SubscriptionDialog";
import UtilService from "../../services/UtilService";
import WordMeaningDialog from "../puzzles/common/WordMeaningDialog";
import HintPatternBoxes from "../puzzles/crossword/components/HintPatternBoxes";
import {
    buildCellToClueNumberMap,
    getFirstPositionForStoryClue,
} from "./storyPlayUtils";
import StoryClueNumberCircle, { STORY_CLUE_CIRCLE_SELECTED_SX } from "./StoryClueNumberCircle";
import {
    findFilledCoordByWord,
    getFoundCellKeySetFromWords,
    getHintPatternForWordCells,
    hintPatternHasRevealedLetter,
    normalizeWord,
    removeHyphens,
} from "../puzzles/common/utils/PuzzlePlayLogic";
import { usePagesTranslation } from "../locales";
import AssistantCreditsEmptyBar from "../puzzles/common/components/AssistantCreditsEmptyBar";
import { ACCENT, SURFACE, TEXT } from "../../dark-theme/palette";
import { playPillButtonSx } from "../../dark-theme/surfaces";

/**
 * Same shell as CrossWordCluesPanel: Word clues + magic AI icon; optional Chat with AI (parity with `/puzzle/play-cross`).
 */
export default function StoryPlaySelectedWordPanel({
    filledCoordinates = [],
    wordsFound = [],
    selectedWord = null,
    hideAllWords = false,
    meaningPreview = "",
    hintRevealCells = [],
    /** @returns {boolean|undefined} Close popover if not false */
    onWordEnterSubmit,
    userPayPlan = null,
    language = "english",
    onHide,
    chatWithAiOpen = false,
    onCloseChatWithAi = () => {},
    assistantMagicVisible: assistantMagicVisibleProp,
    assistantMagicTooltipTitle,
    assistantInputLocked = false,
    assistantLockedInputText = "",
    onOpenChatWithAi,
    assistantMessages = [],
    assistantPrompt = "",
    onAssistantPromptChange = () => {},
    onAssistantSend = () => {},
    assistantLoading = false,
    assistantHistoryLoading = false,
    chatWithAiTitle = "Chat with AI",
    chatWithAiPlaceholder = "",
    assistantFillCellsSlot = null,
    assistantCreditsTotal,
}) {
    const { t, lang: uiLang } = usePagesTranslation();

    const showAssistantMagicButton =
        assistantMagicVisibleProp != null
            ? Boolean(assistantMagicVisibleProp)
            : typeof onOpenChatWithAi === "function";

    const assistantCreditsDepleted =
        !assistantInputLocked &&
        typeof assistantCreditsTotal === "number" &&
        assistantCreditsTotal <= 0;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogWord, setDialogWord] = useState(null);
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [enterPopoverAnchor, setEnterPopoverAnchor] = useState(null);
    const [enterPopoverWord, setEnterPopoverWord] = useState(null);
    const [enterPopoverNumber, setEnterPopoverNumber] = useState(null);
    const [enterPopoverValue, setEnterPopoverValue] = useState("");
    const [localEnterPopoverValue, setLocalEnterPopoverValue] = useState("");
    const enterWordInputRef = useRef(null);

    const debouncedSetEnterPopoverValue = useMemo(
        () =>
            debounce((value) => {
                setEnterPopoverValue(value);
            }, 300),
        []
    );

    useEffect(() => () => debouncedSetEnterPopoverValue.cancel(), [debouncedSetEnterPopoverValue]);

    const foundCellKeySet = useMemo(
        () => getFoundCellKeySetFromWords(filledCoordinates, wordsFound),
        [filledCoordinates, wordsFound]
    );

    const enterPopoverHintPattern = useMemo(() => {
        if (!enterPopoverWord) return null;
        const coord = findFilledCoordByWord(filledCoordinates, enterPopoverWord);
        if (!coord?.positions?.length) return null;
        const wordCells = coord.positions.map((p) => ({ row: p.row, col: p.col }));
        return getHintPatternForWordCells(enterPopoverWord, wordCells, hintRevealCells, foundCellKeySet);
    }, [enterPopoverWord, filledCoordinates, hintRevealCells, foundCellKeySet]);

    const { clueNumber, gridWordForChip, isFound } = useMemo(() => {
        if (!selectedWord || !filledCoordinates?.length) {
            return {
                clueNumber: null,
                gridWordForChip: "",
                isFound: false,
            };
        }
        const coordResolved = findFilledCoordByWord(filledCoordinates, selectedWord);
        const cellMap = buildCellToClueNumberMap(filledCoordinates);
        let n = null;
        if (coordResolved) {
            const first = getFirstPositionForStoryClue(coordResolved);
            if (first) {
                n = cellMap.get(`${first.row},${first.col}`) ?? null;
            }
        }
        const raw = coordResolved?.text != null ? String(coordResolved.text) : "";
        const chipLabel = raw || selectedWord;
        const nw = normalizeWord(selectedWord);
        const found =
            nw && Array.isArray(wordsFound) ? wordsFound.includes(nw) : false;
        return {
            clueNumber: n,
            gridWordForChip: chipLabel,
            isFound: found,
        };
    }, [selectedWord, filledCoordinates, wordsFound]);

    const showWordChip = !hideAllWords || isFound;
    const clueColumnWidth = showWordChip ? "5.25rem" : "2.25rem";
    const showAsFound = isFound;
    const showNumber = clueNumber != null;

    const handleInfoClick = (event) => {
        event.stopPropagation();
        if (!selectedWord) return;
        const hasStandardPlan = UtilService.isStandardPayPlan(userPayPlan);
        if (!hasStandardPlan) {
            setSubscriptionDialogOpen(true);
        } else {
            setDialogWord(selectedWord);
            setDialogOpen(true);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setDialogWord(null);
    };

    const handleEnterIconClick = (event) => {
        event.stopPropagation();
        if (!selectedWord) return;
        debouncedSetEnterPopoverValue.cancel();
        setEnterPopoverAnchor(event.currentTarget);
        setEnterPopoverWord(selectedWord);
        setEnterPopoverNumber(clueNumber ?? null);
        setEnterPopoverValue("");
        setLocalEnterPopoverValue("");
    };

    const handleEnterPopoverClose = () => {
        debouncedSetEnterPopoverValue.cancel();
        setEnterPopoverAnchor(null);
        setEnterPopoverWord(null);
        setEnterPopoverNumber(null);
        setEnterPopoverValue("");
        setLocalEnterPopoverValue("");
    };

    const handleEnterPopoverCheck = () => {
        if (enterPopoverWord && onWordEnterSubmit) {
            debouncedSetEnterPopoverValue.flush();
            const valueToSubmit = localEnterPopoverValue || enterPopoverValue;
            const closePopover = onWordEnterSubmit(enterPopoverWord, valueToSubmit);
            if (closePopover === false) {
                return;
            }
        }
        handleEnterPopoverClose();
    };

    const showHiddenHintInMeaningColumn =
        Boolean(selectedWord) &&
        hideAllWords &&
        !isFound &&
        !String(meaningPreview || "").trim();

    const chatSection = chatWithAiOpen ? (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            {assistantFillCellsSlot ? (
                <Box
                    sx={{
                        flexShrink: 0,
                        maxHeight: "min(42vh, 340px)",
                        overflowY: "auto",
                        borderBottom: `1px solid ${SURFACE.border}`,
                        backgroundColor: SURFACE.sunken,
                    }}
                >
                    {assistantFillCellsSlot}
                </Box>
            ) : null}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                {assistantHistoryLoading && assistantMessages.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : null}
                {assistantMessages.map((m) => {
                    const isUser = String(m.role).toUpperCase() === "USER";
                    return (
                        <Box
                            key={m.id}
                            sx={{
                                alignSelf: isUser ? "flex-end" : "flex-start",
                                maxWidth: "92%",
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 2,
                                bgcolor: isUser ? "#d4edda" : SURFACE.sunken,
                                color: isUser ? "#155724" : TEXT.primary,
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {m.content}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <Box
                sx={{
                    flexShrink: 0,
                    p: 1.25,
                    borderTop: `1px solid ${SURFACE.border}`,
                    backgroundColor: SURFACE.sunken,
                }}
            >
                {assistantCreditsDepleted ? (
                    <Box sx={{ mb: 1.25 }}>
                        <AssistantCreditsEmptyBar
                            message={t(
                                "puzzle.playCross.assistant-credits-empty",
                                "You're out of Coins. Add a subscription or coin pack to keep using the assistant."
                            )}
                            linkLabel={t(
                                "puzzle.playCross.assistant-credits-empty-link",
                                "View plans and coin packs"
                            )}
                        />
                    </Box>
                ) : null}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 1,
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={5}
                        size="small"
                        placeholder={
                            assistantInputLocked
                                ? ""
                                : chatWithAiPlaceholder ||
                                  t("puzzle.playCross.assistant-placeholder", "Message")
                        }
                        value={assistantInputLocked ? assistantLockedInputText : assistantPrompt}
                        onChange={(e) => {
                            if (assistantInputLocked || assistantCreditsDepleted) return;
                            onAssistantPromptChange(e.target.value);
                        }}
                        disabled={assistantLoading || assistantInputLocked || assistantCreditsDepleted}
                        onKeyDown={(e) => {
                            if (assistantInputLocked || assistantCreditsDepleted) return;
                            if (e.key !== "Enter" || e.shiftKey) return;
                            if (e.nativeEvent.isComposing) return;
                            e.preventDefault();
                            if (assistantLoading || !String(assistantPrompt || "").trim()) return;
                            onAssistantSend();
                        }}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={onAssistantSend}
                        disabled={
                            assistantInputLocked ||
                            assistantCreditsDepleted ||
                            assistantLoading ||
                            !String(assistantPrompt || "").trim()
                        }
                        sx={{
                            flexShrink: 0,
                            alignSelf: "flex-end",
                            mb: "2px",
                            textTransform: "none",
                            backgroundColor: "#3BA666",
                            "&:hover": { backgroundColor: "#2E8B57" },
                            "&.Mui-disabled": {
                                backgroundColor: "rgba(59, 166, 102, 0.45)",
                                color: "rgba(255, 255, 255, 0.85)",
                            },
                        }}
                    >
                        {t("puzzle.playCross.assistant-send", "Send")}
                    </Button>
                </Box>
                {assistantLoading || assistantHistoryLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 1,
                            mt: 0.75,
                        }}
                    >
                        <CircularProgress size={22} />
                    </Box>
                ) : null}
            </Box>
        </Box>
    ) : (
        <Box
            sx={{
                flex: 1,
                overflow: "auto",
                p: 1.25,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
            }}
        >
                {!selectedWord ? (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {t(
                            "puzzle.playCross.story-selected-word-placeholder",
                            "Tap a numbered clue in the story or a word on the grid to see it here."
                        )}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            p: 0.5,
                            borderRadius: 1,
                            backgroundColor: ACCENT.soft,
                            border: "1px solid #3BA666",
                            position: "relative",
                            cursor: "default",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                            "&:hover": {
                                "& .info-icon": { opacity: 1 },
                                "& .enter-icon": { opacity: 1 },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                flex: 1,
                                minWidth: 0,
                                position: "relative",
                                pr: 5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.35,
                                    flexShrink: 0,
                                    width: clueColumnWidth,
                                    minWidth: clueColumnWidth,
                                    maxWidth: clueColumnWidth,
                                    boxSizing: "border-box",
                                }}
                            >
                                {showNumber ? (
                                    <StoryClueNumberCircle
                                        sx={selectedWord ? STORY_CLUE_CIRCLE_SELECTED_SX : undefined}
                                    >
                                        {clueNumber}
                                    </StoryClueNumberCircle>
                                ) : null}
                                {showWordChip ? (
                                    <Chip
                                        label={gridWordForChip}
                                        variant={showAsFound ? "filled" : "outlined"}
                                        color={showAsFound ? "success" : "default"}
                                        size="small"
                                        sx={{
                                            backgroundColor: showAsFound ? "#FFEB3B" : undefined,
                                            textDecoration: showAsFound ? "line-through" : "none",
                                            opacity: showAsFound ? 0.9 : 1,
                                            fontWeight: showAsFound ? "bold" : "normal",
                                            color: showAsFound ? "#333" : undefined,
                                            flexShrink: 0,
                                            m: 0,
                                            height: "auto",
                                            maxWidth: "100%",
                                            "& .MuiChip-label": {
                                                fontWeight: showAsFound ? "bold" : "normal",
                                                color: showAsFound ? "#333" : undefined,
                                                px: 0.75,
                                                whiteSpace: "normal",
                                                textAlign: "center",
                                                lineHeight: 1.2,
                                            },
                                            "&.MuiChip-filled": {
                                                backgroundColor: showAsFound ? "#FFEB3B !important" : undefined,
                                                color: showAsFound ? "#333 !important" : undefined,
                                            },
                                        }}
                                    />
                                ) : null}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }} />

                            <Box
                                className="info-icon enter-icon"
                                sx={{
                                    position: "absolute",
                                    right: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.25,
                                    opacity: 0,
                                    transition: "opacity 0.2s",
                                }}
                            >
                                <Tooltip title="More information" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={handleInfoClick}
                                        sx={{
                                            ...playPillButtonSx,
                                            color: ACCENT.light,
                                            p: 0.25,
                                            "&:hover": {
                                                backgroundColor: ACCENT.soft,
                                                color: TEXT.primary,
                                            },
                                        }}
                                    >
                                        <InfoOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        selectedWord
                                            ? t("puzzle.playCross.enter-word", {
                                                  count: removeHyphens(selectedWord || "").length,
                                              })
                                            : ""
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handleEnterIconClick}
                                        sx={{
                                            ...playPillButtonSx,
                                            color: ACCENT.light,
                                            p: 0.25,
                                            "&:hover": {
                                                backgroundColor: ACCENT.soft,
                                                color: TEXT.primary,
                                            },
                                        }}
                                    >
                                        <KeyboardReturnIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        {(meaningPreview || showHiddenHintInMeaningColumn) ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                    pl: 0.25,
                                    pr: 5,
                                }}
                            >
                                {meaningPreview ? (
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                            color: "text.secondary",
                                            fontStyle: "italic",
                                            lineHeight: 1.4,
                                            m: 0,
                                        }}
                                    >
                                        {meaningPreview}
                                    </Typography>
                                ) : null}
                                {showHiddenHintInMeaningColumn ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontStyle: "italic", lineHeight: 1.4 }}
                                    >
                                        {t(
                                            "puzzle.playCross.story-selected-word-hidden-hint",
                                            "The answer is hidden—type it in the crossword grid below."
                                        )}
                                    </Typography>
                                ) : null}
                            </Box>
                        ) : null}
                    </Box>
                )}
        </Box>
    );

    return (
        <Paper
            elevation={3}
            sx={{
                width: "100%",
                height: "100%",
                backgroundColor: SURFACE.card,
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    borderBottom: `1px solid ${SURFACE.border}`,
                    gap: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}>
                    {chatWithAiOpen ? (
                        <>
                            <Tooltip title={t("puzzle.playCross.back-to-clues", "Back to word clues")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={onCloseChatWithAi}
                                    aria-label={t("puzzle.playCross.back-to-clues", "Back to word clues")}
                                    sx={{
                                        color: ACCENT.light,
                                        flexShrink: 0,
                                        "&:hover": {
                                            backgroundColor: ACCENT.soft,
                                            color: TEXT.primary,
                                        },
                                    }}
                                >
                                    <ArrowBackIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ACCENT.light, fontSize: "0.95rem" }}>
                                {chatWithAiTitle}
                            </Typography>
                        </>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ACCENT.light, fontSize: "0.95rem" }}>
                                {t("puzzle.playCross.word-clues")}
                            </Typography>
                            {showAssistantMagicButton ? (
                                <Tooltip
                                    title={
                                        assistantMagicTooltipTitle ||
                                        t("puzzle.playCross.ai-chat-tooltip", "Open Chat with AI in the word list")
                                    }
                                    arrow
                                >
                                    <span>
                                        <IconButton
                                            onClick={() => onOpenChatWithAi?.()}
                                            size="small"
                                            aria-label={t("puzzle.playCross.chat-with-ai", "Chat with AI")}
                                            sx={{
                                                backgroundColor: "secondary.main",
                                                color: "#fff",
                                                width: 28,
                                                height: 28,
                                                minWidth: 28,
                                                minHeight: 28,
                                                borderRadius: "50%",
                                                flexShrink: 0,
                                                "&:hover": {
                                                    backgroundColor: "secondary.dark",
                                                    color: "#fff",
                                                },
                                            }}
                                        >
                                            <AutoAwesomeIcon sx={{ fontSize: 16, color: "#fff" }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : null}
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Tooltip title="Close" arrow>
                        <IconButton
                            onClick={() => onHide?.()}
                            size="small"
                            sx={{
                                color: TEXT.secondary,
                                "&:hover": {
                                    backgroundColor: SURFACE.hover,
                                    color: TEXT.primary,
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {chatSection}

            <WordMeaningDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                word={dialogWord}
                hideWord={true}
                language={language}
                uiLang={uiLang}
            />

            <Popover
                open={Boolean(enterPopoverAnchor)}
                anchorEl={enterPopoverAnchor}
                onClose={handleEnterPopoverClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { p: 1.5, minWidth: 220 } }}
                TransitionProps={{
                    onEntered: () => {
                        const el = enterWordInputRef.current;
                        if (el) {
                            el.focus({ preventScroll: true });
                        }
                    },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {enterPopoverNumber != null && (
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 28,
                                    minHeight: 28,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#C4FFE0",
                                    borderRadius: "50%",
                                    border: "1px solid #2E8B57",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#000",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                }}
                            >
                                {enterPopoverNumber}
                            </Box>
                        )}
                        <TextField
                            size="small"
                            inputRef={enterWordInputRef}
                            placeholder={
                                enterPopoverWord
                                    ? t("puzzle.playCross.enter-word", {
                                          count: removeHyphens(enterPopoverWord).length,
                                      })
                                    : "Enter word"
                            }
                            value={localEnterPopoverValue}
                            inputProps={{
                                maxLength: enterPopoverWord ? removeHyphens(enterPopoverWord).length : 12,
                                style: {
                                    textTransform: "uppercase",
                                    textAlign: "center",
                                    fontSize: "1.1rem",
                                    fontWeight: 600,
                                },
                            }}
                            onChange={(e) => {
                                const v = (e.target.value || "").toUpperCase();
                                setLocalEnterPopoverValue(v);
                                debouncedSetEnterPopoverValue(v);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleEnterPopoverCheck();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 22,
                                                height: 22,
                                                borderRadius: "50%",
                                                backgroundColor: "#e8f5e9",
                                                border: "1px solid #2E8B57",
                                                fontSize: "0.75rem",
                                                fontWeight: 600,
                                                color: "#2E8B57",
                                            }}
                                        >
                                            {localEnterPopoverValue.length}
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: SURFACE.sunken,
                                    "&:hover": {
                                        backgroundColor: SURFACE.card,
                                    },
                                },
                                "& .MuiInputBase-input::placeholder": {
                                    fontSize: "0.875rem",
                                    fontWeight: 400,
                                    opacity: 0.6,
                                    textTransform: "none",
                                },
                            }}
                        />
                        <Tooltip title="Submit" arrow>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleEnterPopoverCheck}
                                sx={{ flexShrink: 0 }}
                            >
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {enterPopoverHintPattern && hintPatternHasRevealedLetter(enterPopoverHintPattern) && (
                        <Box sx={{ pl: enterPopoverNumber != null ? 4.5 : 0, width: "100%" }}>
                            <HintPatternBoxes pattern={enterPopoverHintPattern} boxSize={18} />
                        </Box>
                    )}
                </Box>
            </Popover>

            <SubscriptionDialog
                open={subscriptionDialogOpen}
                onClose={() => setSubscriptionDialogOpen(false)}
                triggerReason="general"
                userSubscription={UtilService.isStandardPayPlan(userPayPlan) ? "premium" : "free"}
            />
        </Paper>
    );
}
